// tslint:disable: completed-docs

import {
    trackIt,
    getSessionStorageItemJson,
    setSessionStorageItemJson,
    getSiteStructure,
    getLoginData,
    getCustomerFlow,
    SelectorReturnType,
} from '@vfde-fe/sails_core';
import {
    selectHasSubscription,
    selectSalesChannel,
    selectSubscriptionId,
} from '../../Tariff/selectors';
import { all, call, select, StrictEffect } from 'redux-saga/effects';
import { selectAtomicDevice } from '../selectors';
import {
    selectHardwareOnly,
    selectName,
    selectOffer,
    selectVendor,
} from '../../DeviceDetail/selectors';
import {
    ADDITIONAL_PAGE_OPTIONS,
    BTX_BNT,
    TRACKING_BUSINESS_TRANSACTION_HARDWARE_ONLY,
    TRACKING_FLOWTYPE_HARDWARE_ONLY,
    TRACKING_FLOWTYPE_PRODUCT_DETAILS,
    TRACKING_FLOWTYPE_STORAGE,
    TRACKING_FLOWTYPE_TARIFE,
    TRACKING_HARDWARE_ONLY,
    TRACKING_LOGGED_IN,
    TRACKING_NEW_CONTRACT,
    TRACKING_NOT_LOGGED_IN,
    TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TRACKING_BUNDLE,
    TRACKING_PRODUCT_LINE,
    TRACKING_PRODUCT_TYPE,
    TRACKING_DEVICE,
    TRACKING_SITE_AREA_MOBILITY,
    TRACKING_TARIFF,
    TRACK_PAGE_VIEW,
    SalesChannel,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_SOHO,
    SALESCHANNEL_REDPLUS,
} from '@vfde-fe/sails-constants';
import { IaProducts } from '../interfaces/tracking';
import { Offer } from 'app/container/DeviceDetail/interfaces/state';

/**
 * Track page view
 */
 export function* trackPageView (): Generator<StrictEffect> {
    const [
        activeSubId,
        activeOffer,
        deviceName,
        vendor,
        atomicDevice,
        isHardwareOnly,
        salesChannel,
        hasSubscription,
    ] = (yield all([
        select(selectSubscriptionId()),
        select(selectOffer()),
        select(selectName()),
        select(selectVendor()),
        select(selectAtomicDevice()),
        select(selectHardwareOnly()),
        select(selectSalesChannel()),
        select(selectHasSubscription()),
    ])) as [
        SelectorReturnType<typeof selectSubscriptionId>,
        SelectorReturnType<typeof selectOffer>,
        SelectorReturnType<typeof selectName>,
        SelectorReturnType<typeof selectVendor>,
        SelectorReturnType<typeof selectAtomicDevice>,
        SelectorReturnType<typeof selectHardwareOnly>,
        SelectorReturnType<typeof selectSalesChannel>,
        SelectorReturnType<typeof selectHasSubscription>,
    ];

    const { pageName } = window[ADDITIONAL_PAGE_OPTIONS];
    const flowType = (yield call(getFlowTypeStorage, hasSubscription, salesChannel as SalesChannel)) as string;

    const trackingData = {
        activeSubId,
        activeOffer,
        pageName,
        salesChannel,
        deviceName,
        vendor,
        flowType,
        atomicDevice,
        isHardwareOnly,
        hasSubscription,
    };

    if (trackingData.atomicDevice) {
        const trackingPayload = createTrackingPageView(trackingData);
        trackIt(TRACK_PAGE_VIEW, trackingPayload);
    }
}

/**
 * Get flowtype from storage
 *
 * @param hasSubscription
 * @param salesChannel
 */
 export function* getFlowTypeStorage (hasSubscription: boolean, salesChannel: SalesChannel): Generator<StrictEffect, string> {
    let flowType = (yield call(getSessionStorageItemJson, TRACKING_FLOWTYPE_STORAGE)) as string | null;

    if (!flowType) {
        flowType = hasSubscription ? TRACKING_FLOWTYPE_TARIFE : TRACKING_FLOWTYPE_PRODUCT_DETAILS;

        switch (salesChannel) {
            case SALESCHANNEL_FAMILY_FRIENDS:
                flowType = `${flowType} fuf`;
                break;
            case SALESCHANNEL_SOHO:
                flowType = `${flowType} soho`;
                break;
            case SALESCHANNEL_REDPLUS:
                flowType = `${flowType} redplus`;
                break;
        }

        yield call(setSessionStorageItemJson, TRACKING_FLOWTYPE_STORAGE, flowType);
    }

    return flowType;
}

/**
 * Create the page view tracking object
 * @param {object} trackingData - Tracking Data
 */
export const createTrackingPageView = (
    trackingData: {
        activeSubId: SelectorReturnType<typeof selectSubscriptionId>;
        activeOffer: SelectorReturnType<typeof selectOffer>;
        pageName: string;
        salesChannel: SelectorReturnType<typeof selectSalesChannel>;
        deviceName: SelectorReturnType<typeof selectName>;
        vendor: SelectorReturnType<typeof selectVendor>;
        flowType: string;
        atomicDevice: SelectorReturnType<typeof selectAtomicDevice>;
        isHardwareOnly: SelectorReturnType<typeof selectHardwareOnly>;
        hasSubscription: SelectorReturnType<typeof selectHasSubscription>;
    },
) => {
    const siteStructure = getSiteStructure(trackingData.pageName, BTX_BNT);
    const loginStatus = getLoginData() ? TRACKING_LOGGED_IN : TRACKING_NOT_LOGGED_IN;
    const lineOfCustomer = getCustomerFlow(trackingData.salesChannel) ;
    const productCategory = trackingData.isHardwareOnly ? TRACKING_DEVICE : TRACKING_BUNDLE;
    const { flowType, atomicDevice } = trackingData;
    const devicePriceOnce = trackingData.isHardwareOnly ? atomicDevice?.totalPrice?.gross : atomicDevice?.oneTimePrice?.gross;
    const { atomicName } = atomicDevice!;
    const parser = new DOMParser();
    let parsedAtomicName = parser.parseFromString(atomicName, 'text/html').body.firstChild?.textContent || '';

    const aProducts: IaProducts[] = [{
        sku: atomicDevice!.backendId,
        name: parsedAtomicName,
        type: TRACKING_DEVICE,
        priceOnce: (devicePriceOnce || 0).toString(),
        proposition: 1,
        units: 1,
    }];

    if (trackingData.hasSubscription && !trackingData.isHardwareOnly) {
        aProducts.push({
            sku: trackingData.activeSubId!.toString(),
            name: (trackingData.activeOffer as Offer).name,
            type: TRACKING_TARIFF,
            priceOnce: (trackingData.activeOffer as Offer).oneTimeFee?.gross.toString(),
            priceMonthly: (trackingData.activeOffer as Offer).tariffPrice?.gross.toString(),
            duration: 24,
            proposition: 1,
            units: 1,
        });

        parsedAtomicName += ` | ${(trackingData.activeOffer as Offer).name}`;
    }

    const trackingPayload = {
        siteStructure,
        loginStatus,
        flowType: trackingData.isHardwareOnly ? TRACKING_FLOWTYPE_HARDWARE_ONLY : flowType,
        lineOfCustomer,
        pageType: TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
        siteArea: TRACKING_SITE_AREA_MOBILITY,
        platformType: TRACKING_PLATFORM_TYPE_RESPONSIVE,
        businessTransactionType: trackingData.isHardwareOnly ? TRACKING_BUSINESS_TRANSACTION_HARDWARE_ONLY : TRACKING_NEW_CONTRACT,
        oOrder: {
            aProducts,
            aPropositions: [{
                id: 1,
                name: parsedAtomicName,
                targetAudience: lineOfCustomer,
                retention: trackingData.isHardwareOnly ? TRACKING_HARDWARE_ONLY : TRACKING_NEW_CONTRACT,
                productType: trackingData.isHardwareOnly ? TRACKING_HARDWARE_ONLY : TRACKING_PRODUCT_TYPE,
                productLine: trackingData.isHardwareOnly ? TRACKING_HARDWARE_ONLY : TRACKING_PRODUCT_LINE,
                productCategory,
                units: 1,
            }],
        },
    };

    return trackingPayload;
};
